import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quickPrompts = [
  'Which Connect plan is right for me?',
  'What\'s the difference between Family and Care?',
  'Can Connect Family include parents too?',
  'How does Connect work without a screen?',
  'What happens after I reserve?',
];

const ConnectGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: text,
          session_id: sessionId 
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      setSessionId(data.session_id);
      
      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I don\'t have that detail yet, but I can help with Connect plans, early access, and how Connect works.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickPrompt = (prompt) => {
    sendMessage(prompt);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: 'spring', stiffness: 200 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#1A1A1A] text-white 
                   shadow-lg hover:bg-[#333] transition-all duration-300 flex items-center justify-center
                   ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[560px] max-h-[calc(100vh-100px)]
                       bg-white rounded-3xl shadow-2xl border border-black/5 flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between bg-gradient-to-b from-[#FAF8F5] to-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] text-sm">Connect Guide</h3>
                  <p className="text-xs text-[#7A7A7A]">Here to help you explore</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors"
              >
                <X className="w-4 h-4 text-[#5A5A5A]" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col justify-center">
                  {/* Welcome Message */}
                  <div className="text-center mb-6">
                    <p className="text-[#4A4A4A] text-sm leading-relaxed">
                      Hi — I'm the Connect Guide. I can help you explore plans, understand how Connect works, and answer questions about early access.
                    </p>
                  </div>
                  
                  {/* Quick Prompts */}
                  <div className="space-y-2">
                    <p className="text-xs text-[#8A8A8A] text-center mb-3">Try asking:</p>
                    {quickPrompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickPrompt(prompt)}
                        className="w-full text-left px-4 py-2.5 bg-[#FAF8F5] rounded-xl text-sm text-[#4A4A4A]
                                   hover:bg-[#F0EBE5] transition-colors border border-black/5"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed
                                   ${msg.role === 'user' 
                                     ? 'bg-[#1A1A1A] text-white rounded-br-md' 
                                     : 'bg-[#FAF8F5] text-[#4A4A4A] rounded-bl-md border border-black/5'}`}
                      >
                        {msg.content}
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Loading indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-[#FAF8F5] px-4 py-3 rounded-2xl rounded-bl-md border border-black/5">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-[#8A8A8A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-[#8A8A8A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-[#8A8A8A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-black/5 bg-white">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about Connect..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-[#FAF8F5] rounded-full text-sm text-[#1A1A1A] 
                             placeholder:text-[#8A8A8A] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10
                             disabled:opacity-50 border border-black/5"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="w-10 h-10 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center
                             hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConnectGuide;
